package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.GemsSavedController
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.GemType
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Browser state for the Shahnameh tales list. */
data class ShahnamehUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val tales: List<Tale> = emptyList(),
    val savedTaleIds: Set<String> = emptySet(),
)

/** Streams the published Shahnameh corpus for the browser list (newest first). */
class ShahnamehViewModel(
    gemsRepository: GemsRepository,
    savedController: GemsSavedController,
) : ViewModel() {

    val state: StateFlow<ShahnamehUiState> =
        combine(gemsRepository.observeTales(), savedController.saved) { result, saved ->
            when (result) {
                is DataResult.Success -> ShahnamehUiState(
                    loading = false,
                    tales = result.data,
                    savedTaleIds = saved.filter { it.type == GemType.Tale }.map { it.id }.toSet(),
                )
                is DataResult.Failure -> ShahnamehUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), ShahnamehUiState())
}

/** Detail state for one tale. */
data class TaleDetailUiState(
    val loading: Boolean = true,
    val error: Boolean = false,
    val tale: Tale? = null,
    val liked: Boolean = false,
    val saved: Boolean = false,
    val signInRequired: Boolean = false,
    val reportStatus: GemReportStatus = GemReportStatus.Idle,
)

/** Loads one Shahnameh tale (deep-link safe) + its like/save/report interactions. */
class TaleDetailViewModel(
    private val gemsRepository: GemsRepository,
    private val savedController: GemsSavedController,
    private val reportRepository: ReportRepository,
    private val taleId: String,
) : ViewModel() {

    private val loaded = MutableStateFlow<DataResult<Tale>?>(null)
    private val signInGate = MutableStateFlow(false)
    private val reportStatus = MutableStateFlow(GemReportStatus.Idle)

    init {
        viewModelScope.launch { loaded.value = gemsRepository.getTale(taleId) }
    }

    val state: StateFlow<TaleDetailUiState> =
        combine(
            loaded,
            gemsRepository.observeLikedTaleIds(),
            savedController.saved,
            signInGate,
            reportStatus,
        ) { result, liked, saved, signIn, report ->
            when (result) {
                null -> TaleDetailUiState(loading = true)
                is DataResult.Success -> TaleDetailUiState(
                    loading = false,
                    tale = result.data,
                    liked = taleId in liked,
                    saved = saved.any { it.type == GemType.Tale && it.id == taleId },
                    signInRequired = signIn,
                    reportStatus = report,
                )
                is DataResult.Failure -> TaleDetailUiState(loading = false, error = true, signInRequired = signIn, reportStatus = report)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), TaleDetailUiState())

    fun toggleLike() {
        val liked = state.value.liked
        viewModelScope.launch {
            val result = gemsRepository.setTaleLiked(taleId, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) signInGate.value = true
        }
    }

    fun toggleSave() {
        viewModelScope.launch { savedController.toggle(SavedGem(id = taleId, type = GemType.Tale)) }
    }

    fun consumeSignInGate() { signInGate.value = false }

    fun report(reason: String) {
        if (reportStatus.value == GemReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = GemReportStatus.Sending
            reportStatus.value = when (val r = reportRepository.fileReport("tale", taleId, reason)) {
                is DataResult.Success -> GemReportStatus.Sent
                is DataResult.Failure ->
                    if (r.error is AppError.Unauthorized) GemReportStatus.SignInRequired else GemReportStatus.Failed
            }
        }
    }

    fun resetReport() { reportStatus.value = GemReportStatus.Idle }
}
